import { useState, useRef, useEffect, ReactNode } from 'react';
import {
  Modal,
  Form,
  Input,
  message,
  Select,
  Upload,
  Image,
  Button,
  Tooltip,
  FormInstance,
  Spin,
} from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { InfoCircleFilled, UploadOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import {
  disputeReturn,
  CommonRefund,
  DisputeReturnRequest,
  refuseReason,
  DisputeOperate,
} from 'services/api/return';
import { useAuthorization } from 'components/Authorization';
import posthog from '@leyan/lytics';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import { useMount, useRequest } from 'ahooks';
import { tolgee } from 'services/i18n/tolgee';
import { getOrderSellerCenter } from 'views/Conversation/Sider/utils';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import { cx, css } from '@emotion/css';
import { match, when } from 'ts-pattern';
import { map, upperCase } from 'lodash-es';
import Icon from '@ant-design/icons';
import { ReactComponent as TranslateSvg } from 'views/Conversation/Sider/assets/autoTranslate.svg';
import { modeTranslate } from 'services/api/overseaTranslate';
import { useRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';

const ImagePreview = styled(Image)`
  display: none;
`;

const ModalDispute = styled(Modal)`
  &.justLookHis {
    .ant-input[disabled],
    .ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
      color: rgba(0, 0, 0, 0.95);
    }
    .ant-modal-footer {
      display: none;
    }
  }
`;

export interface DisputeProps extends CommonRefund {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  internalStoreId: string;
  internalTradeId: string;
  onSuccess?: () => void;
  reqData?: DisputeReturnRequest;
  disputeOperate: DisputeOperate;
  footerExtra?: ReactNode;
  preDisputeContent?: string;
  failReasonCode?: number;
  title?: ReactNode;
  okText?: string;
  region?: General.UpperCaseRegion;
}

const DisputeModal = (props: DisputeProps) => {
  const { t } = useTranslate();
  const {
    internalStoreId,
    externalStoreId,
    internalTradeId,
    externalTradeId,
    returnSn,
    visible,
    setVisible,
    onSuccess,
    reqData,
    disputeOperate,
    footerExtra = <div />,
    preDisputeContent,
    failReasonCode,
    title,
    okText,
    region,
  } = props;
  const { token } = useAuthorization();
  const sysLang = useSystemLanguageValue();
  const [lastEmail, setLastEmail] = useState('');
  const { loading, data: reasons } = useRequest(
    () =>
      refuseReason({
        externalType: 'SHOPEE',
        externalStoreId,
        returnSn,
        externalTradeId,
      }).then((res) => {
        setLastEmail(res.email);
        return res.availableReasonList.map((x) => {
          return {
            ...x,
            value: x.reasonId,
            label: tolgee.t({
              key:
                region === 'BR' ? `disputeReason.BR.${x.reasonId}` : `disputeReason.${x.reasonId}`,
              defaultValue: x.text,
            }),
          };
        });
      }),
    {
      refreshDeps: [returnSn, externalTradeId],
      ready: visible,
    },
  );
  const customUpload = useCustomUpload();

  const [form] = Form.useForm();

  useEffect(() => {
    try {
      const data = JSON.parse(preDisputeContent || '');
      if (!data.email) {
        form.setFieldsValue({
          email: lastEmail,
        });
      }
    } catch (e) {
      form.setFieldsValue({
        email: lastEmail,
      });
    }
  }, [lastEmail, form, preDisputeContent]);

  const uploadButton = (
    <div>
      <UploadOutlined />
      <div style={{ marginTop: 8 }}>{t('trade.上传图片')}</div>
    </div>
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useMount(() => {
    if (!!preDisputeContent) {
      try {
        const data = JSON.parse(preDisputeContent || '');
        form.setFieldsValue({
          email: data.email,
          disputeReasonId: String(data.disputeReasonId),
          disputeTextReason: data.disputeTextReason,
        });
        if (Array.isArray(data.image)) {
          setFileList(
            map(data.image, (v) => ({ url: v, response: { data: { url: v } } } as UploadFile)),
          );
        }
      } catch (e) {}
    }
  });
  if (reqData) {
    form.setFieldsValue(reqData);
  }
  useEffect(() => {
    if (reqData) {
      setFileList(
        reqData.image.map(
          (v) =>
            ({
              url: v,
            } as UploadFile),
        ),
      );
    }
  }, [reqData]);

  const beforeUpload = (file: File) => {
    const isTypeOk = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type);
    if (!isTypeOk) {
      message.error(t('trade.图片格式'));
    }
    const isSizeOk = file.size / 1024 / 1024 <= 1;
    if (!isSizeOk) {
      message.error(t('trade.图片大小', { num: 1 }));
    }
    return isTypeOk && isSizeOk;
  };

  const handleChange = (info: UploadChangeParam<any>) => {
    const { fileList } = info;
    setFileList(fileList);
    const fList: UploadFile[] = [];
    fileList.forEach((v) => {
      if (v.error) {
        message.error(`${t('trade.上传失败')}: ${v.name}`);
      }
      if (v.error || !v.status) {
        return;
      }
      let { url } = v;
      if (v.response?.code === 200) {
        url = v.response?.data?.url;
      }
      fList.push({
        uid: v.uid,
        name: v.name,
        status: v.status,
        url,
      });
    });
    if (fList.length !== fileList.length) {
      setFileList(fList);
    }
  };

  const [selImg, setSelImg] = useState('');
  const handlePreview = async (file: UploadFile) => {
    const url = file?.response?.data?.url || file?.url;
    if (url) {
      setSelImg(url);
      setTimeout(() => {
        if (refImg.current?.firstElementChild) {
          (refImg.current.firstElementChild as HTMLDivElement).click();
        }
      }, 200);
    }
  };

  const { loading: goToSellerCenterLoading, runAsync: goToSellerCenter } = useRequest(
    () => getOrderSellerCenter(internalStoreId, internalTradeId),
    {
      manual: true,
      onError() {
        message.error(t('sider.打开卖家中心失败，请稍后再试'));
      },
    },
  );
  const refImg = useRef<HTMLDivElement>(null);
  const tip = match([disputeOperate, failReasonCode])
    .with(
      [when((t) => t === DisputeOperate.预争议 || t === DisputeOperate.修改预争议), undefined],
      () => {
        return (
          <div className="leading-[18px] text-[12px] text-[#f2a100] bg-[#fffaf0] py-[6px] px-[12px]">
            {t(
              '若您已收到买家退回的货物，并且发现货物有问题，可预填写争议信息，平台状态支持争议时，我们将自动提交争议。',
            )}
          </div>
        );
      },
    )
    .with([when((t) => t === DisputeOperate.向shopee争议), when((t) => !!t)], ([, code]) => {
      const i18nKeyMap: Record<number, string> = {
        5001: '订单完成后发起的退单，不支持发起争议',
        5002: '当前退单状态不可发起争议',
        5003: 'Shopee官方服务出现异常，如有疑问请联系乐聊技术支持人员',
      };
      return (
        <div className="leading-[18px] text-[12px] text-[#f05b5b] bg-[#fff7f7] py-[6px] px-[12px] flex items-center">
          <InfoCircleFilled style={{ fontSize: 16, marginRight: '8px', color: '#F05B5B' }} />
          {t(i18nKeyMap[code as number] || i18nKeyMap[5003])}
        </div>
      );
    })
    .otherwise(() => null);
  const [submitLoading, setSubmitLoading] = useState(false);

  return (
    <ModalDispute
      className={cx(
        reqData ? 'justLookHis' : '',
        css`
          .ant-modal-body {
            padding: 0;
          }
        `,
      )}
      destroyOnClose
      title={title ?? t('trade.向Shopee提出争议')}
      centered
      visible={visible}
      width={560}
      onCancel={() => setVisible(false)}
      footer={
        <div className="flex items-center justify-between">
          {footerExtra}
          <div>
            <Button onClick={() => setVisible(false)}>{t('phrase.取消')}</Button>
            <Button
              loading={submitLoading}
              type="primary"
              onClick={() => {
                form
                  .validateFields()
                  .then((values) => {
                    const image = fileList.map((v) => v?.response?.data?.url);
                    const disputeReasonText = reasons?.find(
                      (x) => x.reasonId === values.disputeReasonId,
                    )?.text;

                    if (
                      !image.length &&
                      [47, 53, 54, 55, '47', '53', '54', '55'].includes(values.disputeReasonId)
                    ) {
                      return message.error(t('phrase.请上传图片'));
                    }

                    setSubmitLoading(true);
                    disputeReturn({
                      externalStoreId,
                      externalTradeId,
                      returnSn,
                      ...values,
                      image,
                      operate: disputeOperate,
                      disputeReasonText,
                    })
                      .then(() => {
                        posthog.capture('$returnRst', {
                          custRreport: {
                            opType: 'DISPUTE',
                            status: 'ok',
                            reqData,
                          },
                        });
                        setVisible(false);
                        message.success(t('ViewShops.保存成功'));
                        onSuccess?.();
                      })
                      .catch((e) => {
                        posthog.capture('$returnRst', {
                          custRreport: {
                            opType: 'DISPUTE',
                            status: 'err',
                            errTip: e.message,
                            reqData,
                          },
                        });
                        if (e.response.data.code === 1029) {
                          try {
                            const json = JSON.parse(e.response.data.msg);
                            const msg = json[sysLang];
                            message.error(msg);
                          } catch (error) {
                            message.error(t('trade.同意争议操作失败'));
                          }
                        } else {
                          message.error(getApiErrorCodeMsg(e.response));
                        }
                      })
                      .finally(() => setSubmitLoading(false));
                  })
                  .catch(() => {});
              }}
            >
              {okText ?? t('trade.提交争议')}
            </Button>
          </div>
        </div>
      }
    >
      {tip}
      <div className="p-[18px]">
        <Form form={form} layout="vertical" autoComplete="off" preserve={false}>
          <Form.Item
            name="disputeReasonId"
            label={t('trade.选择原因')}
            rules={[{ required: true, message: t('trade.请选择原因') }]}
          >
            <Select
              disabled={!!reqData}
              placeholder={t('trade.请选择原因')}
              options={reasons}
              loading={loading}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('trade.电子邮箱')}
            rules={[{ type: 'email', required: true, message: t('trade.邮箱校验') }]}
          >
            <Input disabled={!!reqData} placeholder={t('trade.邮箱提示')} />
          </Form.Item>
          <Form.Item
            name="disputeTextReason"
            label={t('trade.请输入描述')}
            rules={[
              {
                type: 'string',
                required: disputeOperate === DisputeOperate.争议,
                message: t('trade.请输入描述'),
                whitespace: true,
              },
            ]}
          >
            <DescriptionField region={region} form={form} disabled={!!reqData} />
          </Form.Item>
          <Form.Item name="image" label={t('trade.上传图片')}>
            <div className="text-xs text-content-200 mb-2">
              <T
                keyName="Shopee争议上传视频限制提示"
                params={{
                  com: (
                    <Button
                      type="link"
                      className="!p-0 !text-xs"
                      onClick={() => {
                        if (goToSellerCenterLoading) return;
                        goToSellerCenter();
                      }}
                    />
                  ),
                }}
              />
            </div>
            <div className="text-xs text-content-200 mb-2">
              {t('upload.limit', {
                num: 3,
                size: 1,
                type: 'jpg,jpeg,png',
              })}
            </div>
            <Upload
              disabled={!!reqData}
              headers={{
                [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
              }}
              accept=".jpg,.jpeg,.png"
              listType="picture-card"
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              customRequest={(options) =>
                customUpload(options, {
                  storeId: internalStoreId,
                  fileType: 'IMAGE',
                  reqSource: 'REFUND',
                  returnSn,
                })
              }
            >
              {fileList.length >= 3 ? null : uploadButton}
            </Upload>
            <div ref={refImg}>{selImg ? <ImagePreview src={selImg} /> : null}</div>
          </Form.Item>
        </Form>
      </div>
    </ModalDispute>
  );
};

type Props = {
  value?: string;
  onChange?: (v: any) => void;
  region?: General.Region | General.UpperCaseRegion;
  form: FormInstance;
  disabled?: boolean;
  placeholder?: string;
  fieldName?: string;
};
export const DescriptionField = (props: Props) => {
  const {
    value,
    onChange,
    region,
    disabled,
    form,
    placeholder,
    fieldName = 'disputeTextReason',
  } = props;
  const { t } = useTranslate();
  const [transLoading, setTransLoading] = useState(false);
  const regionLanguageMap = useRegionLanguageMap();
  return (
    <div
      className={cx(
        'border border-solid border-[#d9d9d9] rounded-[2px] hover:border-[var(--ant-primary-5)]',
        css`
          &:focus-within {
            box-shadow: 0 0 0 2px var(--ant-primary-color-outline);
          }
        `,
      )}
    >
      {region && (
        <div className="h-[30px] border-0 border-b border-solid border-[#d9d9d9] px-[12px] flex items-center">
          <Tooltip title={t('trade.翻译')}>
            {transLoading ? (
              <Spin size="small" />
            ) : (
              <Icon
                className="cursor-pointer hover:text-[var(--ant-primary-color)]"
                component={TranslateSvg}
                onClick={() => {
                  const v = form.getFieldValue(fieldName);
                  console.log(fieldName, v);
                  if (!v) return;
                  setTransLoading(true);
                  modeTranslate(v, regionLanguageMap[upperCase(region || 'ID') as 'ID'], 'GOOGLE')
                    .then((res) => {
                      form.setFieldsValue({ [fieldName]: res });
                    })
                    .finally(() => {
                      setTransLoading(false);
                    });
                }}
              />
            )}
          </Tooltip>
        </div>
      )}
      <Input.TextArea
        className="[&_textarea]:!border-0 [&_textarea:focus]:!shadow-none [&_textarea:focus]:!border-0 [&_textarea:hover]:!border-0"
        disabled={disabled}
        showCount
        value={value}
        onChange={onChange}
        maxLength={350}
        placeholder={placeholder || t('trade.请输入描述')}
        autoSize={{ minRows: 4, maxRows: 6 }}
      />
    </div>
  );
};

export default DisputeModal;
